import { ROAD } from '../core/meta/tables';
import type { RoadMap, RoadPoint } from './roadPath';

/** Half-width of the packed earth. Grass sits just outside it. */
export const DIRT_HALF = 20;
/** Soft grass edge, still clear of the reward labels. */
export const VERGE_HALF = 31;

export interface RoadSample {
  x: number;
  y: number;
  nx: number;
  ny: number;
}

export interface DirtPatch {
  from: number;
  to: number;
  color: number;
}

export interface Pebble {
  x: number;
  y: number;
  rx: number;
  ry: number;
  color: number;
}

export interface GrassTuft {
  x: number;
  y: number;
  nx: number;
  ny: number;
  len: number;
  lean: number;
  color: number;
}

export interface PawPrint {
  x: number;
  y: number;
  /** Toes point this way, in radians. */
  angle: number;
  /** Prints sit on the center of the dirt, so every step uses the same side. */
  side: 1;
  along: number;
}

/**
 * Clearance box for the print. Pad, then outer/inner/inner/outer toes.
 * Spacing and icon alignment come from this box, so the numbers stay fixed.
 */
export const PAW_PARTS = [
  { x: -12.88, y: 0, rx: 13.88, ry: 14 },
  { x: 1.6, y: -15.2, rx: 2.6, ry: 4.6 },
  { x: 3, y: -5.6, rx: 2.4, ry: 4.8 },
  { x: 3, y: 5.6, rx: 2.4, ry: 4.8 },
  { x: 1.6, y: 15.2, rx: 2.6, ry: 4.6 },
] as const;


/** The previous print was 24.1 long and stepped every 46, so the gap scales with the print. */
const PAW_STEP_RATIO = 46 / 24.1;

export function pawExtent(): { length: number; width: number; halfAlong: number; halfAcross: number } {
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const part of PAW_PARTS) {
    minX = Math.min(minX, part.x - part.rx);
    maxX = Math.max(maxX, part.x + part.rx);
    minY = Math.min(minY, part.y - part.ry);
    maxY = Math.max(maxY, part.y + part.ry);
  }
  return {
    length: maxX - minX,
    width: maxY - minY,
    halfAlong: Math.max(Math.abs(minX), Math.abs(maxX)),
    halfAcross: Math.max(Math.abs(minY), Math.abs(maxY)),
  };
}

/** Target arc from one print to the next, before a segment nudges it to land on the icons. */
export const PAW_STEP = pawExtent().length * PAW_STEP_RATIO;
/** Prints are centered on the dirt. */
export const PAW_LATERAL = 0;

/** Half the drawn reward. Portraits are 84, trick stills are 72, currency icons are 64. */
export function rewardIconRadius(index: number): number {
  const node = ROAD[index];
  if (node?.critter) return 42;
  if (node?.trick) return 36;
  return 32;
}

export interface RoadDressing {
  samples: RoadSample[];
  patches: DirtPatch[];
  pebbles: Pebble[];
  tufts: GrassTuft[];
  leftRut: RoadPoint[];
  rightRut: RoadPoint[];
  paws: PawPrint[];
}

const EARTH = [0xc4894e, 0xb8733a, 0x9a5a2c, 0xa86434, 0x8d4e28] as const;
const STONES = [0xf3e6cf, 0xd7c4a4, 0x8d7760, 0x5e4636] as const;
const GRASS = [0x4f8a32, 0x7eae45, 0x2f6a28] as const;

function hash01(index: number, salt: number): number {
  let n = Math.imul((index + 1) ^ Math.imul(salt + 3, 0x45d9f3b), 0x9e3779b1);
  n = Math.imul(n ^ (n >>> 16), 0x7feb352d);
  n = Math.imul(n ^ (n >>> 15), 0x846ca68b);
  return ((n ^ (n >>> 16)) >>> 0) / 4294967296;
}

function pick<T>(list: readonly T[], index: number, salt: number): T {
  return list[Math.floor(hash01(index, salt) * list.length)] as T;
}

/** Even steps in arc length, from the tail under the first node to the top. */
export function centerline(map: RoadMap, count: number, spacing = 8): RoadSample[] {
  const start = -map.tail;
  const end = Math.max(0, count - 1);
  const probe = Math.max(24, count * 48);
  const fine: RoadPoint[] = [];
  for (let step = 0; step <= probe; step += 1) {
    fine.push(map.point(start + ((end - start) * step) / probe));
  }
  const points: RoadPoint[] = [fine[0] as RoadPoint];
  let carry = 0;
  for (let index = 1; index < fine.length; index += 1) {
    const prev = fine[index - 1] as RoadPoint;
    const next = fine[index] as RoadPoint;
    const span = Math.hypot(next.x - prev.x, next.y - prev.y);
    carry += span;
    if (carry >= spacing || index === fine.length - 1) {
      points.push(next);
      carry = 0;
    }
  }
  return points.map((point, index) => {
    const prev = points[Math.max(0, index - 1)] as RoadPoint;
    const next = points[Math.min(points.length - 1, index + 1)] as RoadPoint;
    let dx = next.x - prev.x;
    let dy = next.y - prev.y;
    const length = Math.hypot(dx, dy) || 1;
    dx /= length;
    dy /= length;
    return { x: point.x, y: point.y, nx: -dy, ny: dx };
  });
}

function along(samples: RoadSample[], from: number, to: number, t: number): RoadSample {
  const a = samples[from] as RoadSample;
  const b = samples[to] as RoadSample;
  const nx = a.nx + (b.nx - a.nx) * t;
  const ny = a.ny + (b.ny - a.ny) * t;
  const length = Math.hypot(nx, ny) || 1;
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    nx: nx / length,
    ny: ny / length,
  };
}

/** One continuous dressing. Spacing and color come from a hash, not a repeated stamp. */
export function dressRoad(map: RoadMap, count: number): RoadDressing {
  const samples = centerline(map, count);
  const patches: DirtPatch[] = [];
  let cursor = 0;
  let patch = 0;
  while (cursor < samples.length - 1) {
    const run = 2 + Math.floor(hash01(patch, 1) * 5);
    const to = Math.min(samples.length - 1, cursor + run);
    patches.push({ from: cursor, to, color: pick(EARTH, patch, 2) });
    cursor = to;
    patch += 1;
  }

  const pebbles: Pebble[] = [];
  const tufts: GrassTuft[] = [];
  const leftRut: RoadPoint[] = [];
  const rightRut: RoadPoint[] = [];
  const first = samples[0];
  if (first) {
    leftRut.push({ x: first.x - first.nx * 7.5, y: first.y - first.ny * 7.5 });
    rightRut.push({ x: first.x + first.nx * 7.5, y: first.y + first.ny * 7.5 });
  }
  let walked = 0;
  let nextPebble = 40 + hash01(0, 4) * 48;
  let nextTuft = 16 + hash01(0, 5) * 18;
  let pebble = 0;
  let tuft = 0;
  for (let index = 1; index < samples.length; index += 1) {
    const prev = samples[index - 1] as RoadSample;
    const next = samples[index] as RoadSample;
    const span = Math.hypot(next.x - prev.x, next.y - prev.y);
    const start = walked;
    const end = walked + span;
    while (nextPebble <= end) {
      const t = span <= 0 ? 0 : (nextPebble - start) / span;
      const at = along(samples, index - 1, index, t);
      const side = hash01(pebble, 6) > 0.5 ? 1 : -1;
      const across = side * DIRT_HALF * (0.62 + hash01(pebble, 17) * 0.3);
      pebbles.push({
        x: at.x + at.nx * across,
        y: at.y + at.ny * across,
        rx: 1.3 + hash01(pebble, 7) * 1.9,
        ry: 1.0 + hash01(pebble, 8) * 1.6,
        color: pick(STONES, pebble, 9),
      });
      pebble += 1;
      nextPebble += 36 + hash01(pebble, 4) * 40;
    }
    while (nextTuft <= end) {
      const t = span <= 0 ? 0 : (nextTuft - start) / span;
      const at = along(samples, index - 1, index, t);
      const side = hash01(tuft, 10) > 0.5 ? 1 : -1;
      const out = DIRT_HALF + 1 + hash01(tuft, 11) * 3;
      tufts.push({
        x: at.x + at.nx * side * out,
        y: at.y + at.ny * side * out,
        nx: at.nx * side,
        ny: at.ny * side,
        len: 11 + hash01(tuft, 12) * 8,
        lean: (hash01(tuft, 13) - 0.5) * 0.9,
        color: pick(GRASS, tuft, 14),
      });
      tuft += 1;
      nextTuft += 18 + hash01(tuft, 5) * 26;
    }
    const wobble = (hash01(index, 15) - 0.5) * 2.2;
    const rut = 7.5;
    leftRut.push({ x: next.x + next.nx * (-rut + wobble), y: next.y + next.ny * (-rut + wobble) });
    rightRut.push({ x: next.x + next.nx * (rut + wobble * 0.6), y: next.y + next.ny * (rut + wobble * 0.6) });
    walked = end;
  }
  return { samples, patches, pebbles, tufts, leftRut, rightRut, paws: pawTrail(map, count, samples) };
}

function distances(samples: RoadSample[]): number[] {
  const cum = [0];
  for (let index = 1; index < samples.length; index += 1) {
    const prev = samples[index - 1] as RoadSample;
    const next = samples[index] as RoadSample;
    cum.push((cum[index - 1] ?? 0) + Math.hypot(next.x - prev.x, next.y - prev.y));
  }
  return cum;
}

function atDistance(samples: RoadSample[], cum: number[], distance: number): RoadSample | null {
  const last = cum[cum.length - 1] ?? 0;
  if (distance < 0 || distance > last || samples.length < 2) return null;
  let lo = 1;
  let hi = cum.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((cum[mid] ?? 0) < distance) lo = mid + 1;
    else hi = mid;
  }
  const span = (cum[lo] ?? 0) - (cum[lo - 1] ?? 0);
  const t = span <= 0 ? 0 : (distance - (cum[lo - 1] ?? 0)) / span;
  return along(samples, lo - 1, lo, Math.min(1, Math.max(0, t)));
}

function arcOf(samples: RoadSample[], cum: number[], x: number, y: number): number {
  let best = 0;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (let index = 1; index < samples.length; index += 1) {
    const prev = samples[index - 1] as RoadSample;
    const next = samples[index] as RoadSample;
    const dx = next.x - prev.x;
    const dy = next.y - prev.y;
    const span = dx * dx + dy * dy || 1;
    const t = Math.min(1, Math.max(0, ((x - prev.x) * dx + (y - prev.y) * dy) / span));
    const px = prev.x + t * dx;
    const py = prev.y + t * dy;
    const distance = Math.hypot(x - px, y - py);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = (cum[index - 1] ?? 0) + t * Math.hypot(dx, dy);
    }
  }
  return best;
}

function nodeFrame(map: RoadMap, index: number): RoadSample {
  const point = map.point(index);
  const dx = -map.amp * Math.PI * Math.sin(Math.PI * index);
  const dy = -map.stride;
  const length = Math.hypot(dx, dy) || 1;
  const fx = dx / length;
  const fy = dy / length;
  return { x: point.x, y: point.y, nx: -fy, ny: fx };
}

function makePrint(at: RoadSample, alongDist: number, angle = Math.atan2(-at.nx, at.ny)): PawPrint {
  return {
    x: at.x,
    y: at.y,
    angle,
    side: 1,
    along: alongDist,
  };
}

/** How many even gaps fit the segment without a print clipping an icon. */
function intervalsFor(length: number, target: number, minStep: number): number {
  if (length <= 0) return 1;
  let best = 1;
  let bestScore = Number.POSITIVE_INFINITY;
  const cap = Math.max(1, Math.ceil(length / Math.max(minStep, 1)));
  for (let count = 1; count <= cap; count += 1) {
    const step = length / count;
    if (step + 1e-4 < minStep) continue;
    const score = Math.abs(step - target);
    if (score < bestScore) {
      bestScore = score;
      best = count;
    }
  }
  return best;
}

/**
 * Even steps up the path. The count in each stretch is chosen so a print
 * sits on every reward icon and the neighbors stop short of the icon edge.
 */
function pawTrail(map: RoadMap, count: number, samples: RoadSample[]): PawPrint[] {
  const cum = distances(samples);
  const { halfAlong } = pawExtent();
  const margin = 6;
  const nodeArc = Array.from({ length: count }, (_, index) => {
    const point = map.point(index);
    return arcOf(samples, cum, point.x, point.y);
  });
  const paws: PawPrint[] = [];
  const tail = nodeArc[0] ?? 0;
  const tailMin = rewardIconRadius(0) + halfAlong + margin;
  let tailIntervals = tail < tailMin ? 0 : Math.round(tail / PAW_STEP);
  while (tailIntervals > 0 && tail / tailIntervals < tailMin) tailIntervals -= 1;
  if (tailIntervals > 0) {
    const step = tail / tailIntervals;
    for (let stepIndex = 0; stepIndex < tailIntervals; stepIndex += 1) {
      const dist = stepIndex * step;
      const at = atDistance(samples, cum, dist);
      if (at) paws.push(makePrint(at, dist));
    }
  }
  for (let index = 0; index < count; index += 1) {
    const spot = nodeFrame(map, index);
    const frame = atDistance(samples, cum, nodeArc[index] ?? 0) ?? spot;
    paws.push(makePrint(spot, nodeArc[index] ?? 0, Math.atan2(-frame.nx, frame.ny)));
    if (index === count - 1) break;
    const length = (nodeArc[index + 1] ?? 0) - (nodeArc[index] ?? 0);
    const minStep = Math.max(rewardIconRadius(index), rewardIconRadius(index + 1)) + halfAlong + margin;
    const gaps = intervalsFor(length, PAW_STEP, minStep);
    const step = length / gaps;
    for (let stepIndex = 1; stepIndex < gaps; stepIndex += 1) {
      const dist = (nodeArc[index] ?? 0) + stepIndex * step;
      const at = atDistance(samples, cum, dist);
      if (at) paws.push(makePrint(at, dist));
    }
  }
  return paws;
}
